"use client"

import * as React from "react"
import Image from "next/image"
import { ChevronLeft, ChevronRight } from "lucide-react"

import type { GalleryImage } from "@/features/catalog/catalog"
import { cn } from "@/lib/utils"

const ARROW =
  "bg-void/90 text-bone hover:border-blaze hover:text-blaze absolute top-1/2 z-10 grid size-9 -translate-y-1/2 place-items-center rounded-full border border-white/20 shadow-[0_6px_18px_rgb(0_0_0_/_0.55)] transition-[opacity,color,border-color] disabled:pointer-events-none disabled:opacity-0"

/** A first slide that is not a photograph: `view` in the frame, `thumb` in the row. */
export type GalleryLead = { view: React.ReactNode; thumb: string; alt: string }

/** The product page's pictures: one large, a row of thumbnails under it. */
export function ProductGallery({
  images,
  shot,
  onShot,
  lead,
  badges,
}: {
  images: GalleryImage[]
  shot: number
  onShot: (index: number) => void
  lead?: GalleryLead
  /** Over the top-left of the large picture. */
  badges?: React.ReactNode
}) {
  const thumbsList = [
    ...(lead ? [{ src: lead.thumb, alt: lead.alt }] : []),
    ...images.map((image) => ({ src: image.src, alt: image.alt })),
  ]
  const index = Math.min(shot, thumbsList.length - 1)
  const active = lead && index === 0 ? null : images[index - (lead ? 1 : 0)]!

  // The thumbnail row scrolls within itself; the arrows step one thumbnail at a time.
  const thumbs = React.useRef<HTMLDivElement>(null)
  const [canScroll, setCanScroll] = React.useState({ back: false, forward: false })
  const measureThumbs = React.useCallback(() => {
    const el = thumbs.current
    if (!el) return
    setCanScroll({
      back: el.scrollLeft > 1,
      forward: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    })
  }, [])
  React.useEffect(() => {
    measureThumbs()
    window.addEventListener("resize", measureThumbs)
    return () => window.removeEventListener("resize", measureThumbs)
  }, [measureThumbs, thumbsList.length])
  const stepThumbs = (direction: 1 | -1) => {
    const el = thumbs.current
    const first = el?.firstElementChild as HTMLElement | null
    if (!el || !first) return
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0
    el.scrollBy({ left: direction * (first.offsetWidth + gap), behavior: "smooth" })
  }
  // The picked shot stays in view.
  React.useEffect(() => {
    const el = thumbs.current
    const thumb = el?.children[index] as HTMLElement | undefined
    if (!el || !thumb) return
    if (thumb.offsetLeft < el.scrollLeft) {
      el.scrollTo({ left: thumb.offsetLeft, behavior: "smooth" })
    } else if (thumb.offsetLeft + thumb.offsetWidth > el.scrollLeft + el.clientWidth) {
      el.scrollTo({
        left: thumb.offsetLeft + thumb.offsetWidth - el.clientWidth,
        behavior: "smooth",
      })
    }
  }, [index])

  return (
    <div className="flex flex-col gap-3.5">
      {/* Square: the colourway shots are 1:1. */}
      <div className="grain rounded-card bg-carbon relative aspect-square w-full overflow-hidden border border-white/[0.08]">
        {/* On desktop its column is capped at the viewport height, and sizes must say so or
            desktop downloads 2-4x the pixels it shows. */}
        {active ? (
          <Image
            key={active.src}
            src={active.src}
            alt={active.alt}
            fill
            preload
            fetchPriority="high"
            sizes="(min-width: 1024px) min(55vw, calc(100vh - 19rem)), 92vw"
            className="object-cover"
          />
        ) : (
          lead!.view
        )}
        {badges ? <div className="absolute top-4 left-4 flex flex-wrap gap-2">{badges}</div> : null}
        {active?.caption ? (
          <div className="absolute inset-x-0 bottom-0 bg-[linear-gradient(0deg,rgb(7_6_10_/_0.92)_0%,rgb(7_6_10_/_0.7)_45%,rgb(7_6_10_/_0)_100%)] px-5 pt-16 pb-5 sm:px-7 sm:pb-6">
            <p className="font-display text-bone text-[22px] leading-[1.1] uppercase sm:text-[28px]">
              {active.caption.title}
            </p>
            <p className="text-ash mt-1.5 max-w-[460px] text-[14px] leading-[1.55] sm:text-[15px]">
              {active.caption.body}
            </p>
          </div>
        ) : null}
      </div>

      {/* Five thumbnails from sm, three on a phone, across the picture's full width. The row
          scrolls within itself, never the page. Height is capped: square thumbs push the gallery
          past the fold. The arrows sit on its edges (half out, inside the page's 20px gutter) and
          show only while there is more that way. */}
      <div className="relative">
        <div
          ref={thumbs}
          onScroll={measureThumbs}
          className="flex snap-x [scrollbar-width:none] gap-2 overflow-x-auto sm:gap-2.5 [&::-webkit-scrollbar]:hidden"
        >
          {thumbsList.map((g, i) => (
            <button
              key={g.src + i}
              type="button"
              onClick={() => onShot(i)}
              aria-label={`View ${g.alt}`}
              aria-current={i === index}
              className={cn(
                "relative aspect-square max-h-[84px] w-[calc((100%-1rem)/3)] shrink-0 snap-start overflow-hidden rounded-xl border transition-colors sm:w-[calc((100%-2.5rem)/5)]",
                i === index ? "border-blaze" : "border-white/10 hover:border-white/25",
              )}
            >
              <Image src={g.src} alt="" fill sizes="120px" className="object-cover" />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => stepThumbs(-1)}
          disabled={!canScroll.back}
          aria-label="Previous pictures"
          className={cn(ARROW, "left-0 -translate-x-1/2")}
        >
          <ChevronLeft className="size-4" strokeWidth={2.2} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => stepThumbs(1)}
          disabled={!canScroll.forward}
          aria-label="More pictures"
          className={cn(ARROW, "right-0 translate-x-1/2")}
        >
          <ChevronRight className="size-4" strokeWidth={2.2} aria-hidden />
        </button>
      </div>
    </div>
  )
}
